import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom, Observable } from 'rxjs';
import {
  AffectedPackagingDialogComponent, AffectedPackagingDialogData
} from '../components/dialogs/affected-packaging-dialog/affected-packaging-dialog.component';
import { SimpleDialogData } from '../components/dialogs/simple-alert-dialog/simple-alert-dialog.component';
import { SimpleConfirmDialogComponent } from '../components/dialogs/simple-confirm-dialog/simple-confirm-dialog.component';
import { AffectedPackagingDto } from '../data-transfer/entities/affected-packaging-dto';
import { PpwrDocumentHandlingDto } from '../data-transfer/entities/ppwr-entities/ppwr-document-handling-dto';
import { DialogActions } from '../model/dictionary';
import { getDialogConfig } from './dialog-util';

export interface AffectedPackagingConfirmation {
  proceed: boolean;
  handling?: PpwrDocumentHandlingDto;
}

/**
 * Shows all packaging which is affected if the given packaging is updated and asks the user to confirm the save
 * and, if any affected packaging has PPWR documents, whether those should be deleted or recreated.
 * Returns proceed = true if the packaging is new, if nothing else is affected or if the user confirmed.
 */
export async function confirmAffectedPackaging(
  dialog: MatDialog,
  translateService: TranslocoService,
  packagingId: number | undefined,
  affectedPackagingFunction: (packagingId: number, stayOnPageIfError: boolean) => Observable<AffectedPackagingDto[]>,
  allowRecreate: boolean
): Promise<AffectedPackagingConfirmation> {
  if (packagingId == null) { return { proceed: true }; }
  let affectedPackaging: AffectedPackagingDto[];
  try {
    affectedPackaging = await firstValueFrom(affectedPackagingFunction(packagingId, true));
  } catch (e) {
    console.error('Could not load affected packaging, ', e);
    // Without the list the save would drop the generated documents unannounced, so the user decides without it.
    // A missing read permission is worth naming separately, as only that one tells the user what to ask for.
    const cause = (e as HttpErrorResponse)?.status === 403
      ? 'affectedPackaging.errors.loadForbidden'
      : 'affectedPackaging.errors.loadFailed';
    const dialogData: SimpleDialogData = {
      title: translateService.translate('common.text.warning'),
      messages: [
        translateService.translate(cause),
        translateService.translate('affectedPackaging.errors.saveAnyway')
      ],
      icon: 'warning'
    };
    const errorResult = await firstValueFrom(
      dialog.open(SimpleConfirmDialogComponent, getDialogConfig(dialogData, '400px')).afterClosed()
    );
    return { proceed: errorResult?.event === DialogActions.CONFIRM };
  }
  if (!affectedPackaging || affectedPackaging.length === 0) { return { proceed: true }; }
  const dialogData: AffectedPackagingDialogData = { affectedPackaging, allowRecreate };
  const dialogResult = await firstValueFrom(
    dialog.open(AffectedPackagingDialogComponent, getDialogConfig(dialogData, '1300px')).afterClosed()
  );
  if (dialogResult?.event !== DialogActions.CONFIRM) { return { proceed: false }; }
  return { proceed: true, handling: dialogResult.handling };
}
